import { type Address } from 'viem'

interface UserAccountData {
  id?: string
  wallet_address: string
  refer_code?: string
  invite_code?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  joined_on?: string
  last_login_time?: string
  odds_preference?: 'decimal' | 'fractional' | 'moneyline'
  signin_token?: string
  signed_msg_id?: string
  last_signin_at?: string
  login_toolkit?: 'privy' | 'rainbow'
  login_method?: 'wallet' | 'social'
  referralStatistics?: {
    betCount: string
    betVolume: string
    userCount: string
  }
  new_account?: boolean
  first_name?: string
  last_name?: string
  email_id?: string
  profile_image?: string
  is_verified?: boolean
  twitter?: {
    profilePictureUrl?: string
  }
}

interface PrivyUserData {
  did: string // Privy DID
  createdAt: Date // Timestamp
  linkedAccounts: string // JSON string
  email: string | null // JSON string
  phone: string | null // JSON string
  wallet: string // JSON string
  google: string | null // JSON string
  apple: string | null // JSON string
  twitter: string | null // JSON string
  discord: string | null // JSON string
  telegram: string | null // JSON string
  github: string | null // JSON string
  linkedin: string | null // JSON string
  spotify: string | null // JSON string
  instagram: string | null // JSON string
  tiktok: string | null // JSON string
  farcaster: string | null // JSON string
  delegatedWallets: string // JSON string
  mfaMethods: string // JSON string
  hasAcceptedTerms: boolean
  isGuest: boolean
  wallet_address: string
}

interface UserAccountRequest {
  wallet_address?: Address | undefined
  refer_code?: string
  invite_code?: string
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_term?: string
  utm_content?: string
  joined_on?: string
  last_login_time?: string
  odds_preference?: 'decimal' | 'fractional' | 'moneyline'
  signin_token?: string
  signed_msg_id?: string
  last_signin_at?: string
  login_toolkit?: 'privy' | 'rainbow'
  login_method?: 'wallet' | 'social'
  // Optional parameters that match the schema
}

interface PrivyUserResponse {
  data: PrivyUserData | null
}

type WalletAccount = {
  type: string
  connectorType?: string
  walletClientType?: string
  chainType?: string
  address?: string
}

type User = {
  linkedAccounts?: WalletAccount[]
}

// Get the embedded or external wallet based on description
const getWalletAddress = (user: User): string | undefined => {
  const walletAccount = user.linkedAccounts?.find((account: any) => {
    if (account.type !== 'wallet') {
      return false
    }

    // Check for embedded wallet
    if (
      account.connectorType === 'embedded' &&
      account.walletClientType === 'privy' &&
      account.chainType === 'ethereum'
    ) {
      return true
    }

    // Check for external wallet
    if (
      account.connectorType === 'injected' &&
      account.walletClientType === 'privy' &&
      account.chainType === 'ethereum'
    ) {
      return true
    }

    return false
  })

  return walletAccount?.address?.toLowerCase()
}

// Get email from linked accounts in sequence
const getEmail = (user: any) => {
  const emailTypes = ['email', 'google_oauth', 'apple_oauth', 'discord']
  const account = user.linkedAccounts?.find(
    (acc: any) => emailTypes.includes(acc.type) && acc.address
  )
  return account?.address?.toLowerCase()
}

// Get name from linked accounts in sequence
const getName = (user: any) => {
  const nameAccounts = [
    'google',
    'twitter',
    'linkedin',
    'tiktok',
    'github',
    'telegram',
    'farcaster',
  ]
  for (const type of nameAccounts) {
    const account = user.linkedAccounts?.find((acc: any) => acc.type === type)
    if (account?.displayName) {
      return account.displayName.split(' ')
    }
  }
  return [null, null]
}

// Helper to get high-res Twitter image
function getHighResTwitterImage(url: string | null | undefined) {
  if (!url) {
    return url
  }
  return url.replace(/_normal|_bigger|_mini/, '')
}

// Get profile image from linked accounts
const getProfileImage = (user: any) => {
  const imageAccounts = ['twitter', 'telegram']
  for (const type of imageAccounts) {
    const account = user.linkedAccounts?.find((acc: any) => acc.type === type)
    if (account?.profilePictureUrl || account?.photoUrl) {
      const url = account.profilePictureUrl || account.photoUrl
      return type === 'twitter' ? getHighResTwitterImage(url) : url
    }
    if (user.twitter) {
      return getHighResTwitterImage(user.twitter?.profilePictureUrl)
    }
  }
  return null
}

// Check email verification status and get timestamps
const getEmailVerification = (user: any) => {
  const emailAccount = user.linkedAccounts?.find(
    (acc: any) =>
      acc.type === 'email' ||
      acc.type === 'google_oauth' ||
      acc.type === 'apple_oauth' ||
      acc.type === 'discord'
  )
  return {
    verify_email_triggered: emailAccount?.firstVerifiedAt || null,
    is_verified: emailAccount?.verifiedAt ? true : false,
  }
}

function getChainName(chain: string): string {
  const chainMap = new Map([
    ['chiliz spicy testnet', 'chiliz'],
    ['chiliz chain', 'chiliz'],
    ['base', 'base'],
    ['base sepolia', 'base'],
  ])

  return chainMap.get(chain.trim().toLowerCase()) || 'polygon'
}

function getTokenName(chain: string): string {
  const validTokens = new Set(['wchz', 'weth'])
  return validTokens.has(chain.toLowerCase()) ? chain.toLowerCase() : 'usdt'
}

function encodeBase64UrlSafe(obj) {
  const jsonString = JSON.stringify(obj)
  const utf8Bytes = new TextEncoder().encode(jsonString) // Convert to UTF-8 bytes
  const base64String = btoa(String.fromCharCode(...utf8Bytes)) // Convert bytes to base64
    .replace(/\+/g, '-') // Replace "+" with "-"
    .replace(/\//g, '_') // Replace "/" with "_"
    .replace(/=+$/, '') // Remove trailing "="

  return base64String
}

const formatToFixed = (value: string | number, digitsCount: number): string => {
  value = String(value)

  if (!/\./.test(value)) {
    return value
  }

  const [int, digits] = value.split('.')

  if (digitsCount) {
    return int + '.' + digits.substring(0, digitsCount)
  }

  return int
}

const decimalToFraction = value => {
  if (value == parseInt(value)) {
    return `${parseInt(value)}/1`
  }

  const eps = 1.0e-4
  let h, h1, h2, k, k1, k2, a, x

  x = +value - 1
  a = Math.floor(x)
  h1 = 1
  k1 = 0
  h = a
  k = 1

  while (x - a > eps * k * k) {
    x = 1 / (x - a)
    a = Math.floor(x)
    h2 = h1
    h1 = h
    k2 = k1
    k1 = k
    h = h2 + a * h1
    k = k2 + a * k1
  }

  return h + '/' + k
}

const decimalToAmerican = (value: number) => {
  let multiplier = 100

  if (value < 2) {
    multiplier = -100

    return formatToFixed(multiplier / (value - 1), 0)
  }

  return `+${formatToFixed((value - 1) * multiplier, 0)}`
}

const formatViewValue = (oddsView: string, value: string | number) => {
  if (oddsView === 'fractional') {
    return decimalToFraction(value)
  }

  if (oddsView === 'moneyline') {
    return decimalToAmerican(+value).toString()
  }
  if (oddsView === 'decimalWith3Decimals') {
    return formatToFixed(value, 3).toString()
  }
  if (oddsView === 'decimalWith5Decimals') {
    return formatToFixed(value, 5).toString()
  }
  // default to decimal with 2 decimals
  return formatToFixed(value, 2).toString()
}

const toKFormat = (num: number): string => {
  const formatter = Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: 1,
  })
  return formatter.format(num)
}

export {
  getWalletAddress,
  getEmail,
  getName,
  getProfileImage,
  getEmailVerification,
  getTokenName,
  getChainName,
  encodeBase64UrlSafe,
  formatViewValue,
  decimalToFraction,
  decimalToAmerican,
  toKFormat,
  getHighResTwitterImage,
}

export type {
  UserAccountData,
  PrivyUserData,
  PrivyUserResponse,
  UserAccountRequest,
}
